import React, {useEffect, useState} from "react";
import {useAuthStore} from "../store/authStore";
import {useTokenFromURL} from "../hooks/useTokenFromURL";
import {clearAuth} from "../utils/auth";
import {auth} from "../firebaseConfig";
import {useTemplatesInit} from "../hooks/useTemplatesInit";
import {AppBar, Box, CssBaseline, Toolbar, Typography, useMediaQuery, useTheme} from "@mui/material";
import Encounter from "../components/Encounter/Encounter";
import EncountersList from "../components/Encounter/EncountersList";
import TemplatesList from "../components/Template/TemplatesList";
import {Sidebar} from "../components/App/Sidebar";
import MicIcon from "@mui/icons-material/Mic";
import HistoryIcon from "@mui/icons-material/History";
import DescriptionIcon from "@mui/icons-material/Description";
import {useFirebaseTokenListener} from "../hooks/useFirebaseTokenListener";
import GlobalSnackbar from "../components/App/GlobalSnackbar";
import {useUserInit} from "../hooks/useUserInit";
import UserAgreement from "../components/App/UserAgreement";
import Login from "./Login";
import {useAppStore} from "../store/appStore";
import {showMessage} from "../utils/snackbar";
import PrivacyPolicy from "../components/App/PrivacyPolicy";
import UsageInstructions from "../components/App/UsageInstructions";
import {useHubSpot} from "../hooks/useHubSpot";
import medendoLogo from "../assets/medendo_logo.png";
import {useSentryUser} from "../hooks/useSentyUser";
import {SESSION_STORAGE_SHOW_UPDATE_NOTIFICATION_KEY} from "../api/api";
import {useHandleVisibility} from "../hooks/useHandleVisibility";
import {tokenRefresher} from "../services/firebase/tokenRefresher";

export const drawerWidth = 230;

export enum MenuItem {
    NewEncounter = "Opnemen",
    History = "Gesprekken",
    Templates = "Templates",
}

export const menuIcons: Record<MenuItem, JSX.Element> = {
    [MenuItem.NewEncounter]: <MicIcon sx={{mr: '8px'}}/>,
    [MenuItem.History]: <HistoryIcon sx={{mr: '8px'}}/>,
    [MenuItem.Templates]: <DescriptionIcon sx={{mr: '8px'}}/>,
};


const App: React.FC = () => {
    const [selectedMenuItem, setSelectedMenu] = useState<MenuItem>(MenuItem.NewEncounter);
    const [timestampKey, setTimestampKey] = useState<number>(0);
    const [sidebarOpen, setSidebarOpen] = useState<boolean>(true);
    const isIntegration = useAppStore((state) => state.isIntegration);
    const idToken = useAuthStore((state) => state.idToken);
    const userModel = useAppStore((state) => state.userModel);

    const theme = useTheme();
    const isLargeScreen = useMediaQuery(theme.breakpoints.up("md"));

    // Initialize sidebar state based on screen size
    useEffect(() => {
        setSidebarOpen(isLargeScreen && !isIntegration);
    }, [isLargeScreen, isIntegration]);

    useEffect(() => {
        const shouldShowNotification = sessionStorage.getItem(SESSION_STORAGE_SHOW_UPDATE_NOTIFICATION_KEY);
        if (shouldShowNotification) {
            showMessage("Applicatie bijgewerkt naar de nieuwste versie.", "info");
            sessionStorage.removeItem(SESSION_STORAGE_SHOW_UPDATE_NOTIFICATION_KEY); // Clean up the flag
        }
    }, []);

    useEffect(() => {
        console.info("[App] Initializing token refresher. Integration: ", isIntegration);
        tokenRefresher.isIntegration = isIntegration
        tokenRefresher.start()
    }, [isIntegration]);

    useFirebaseTokenListener();

    useTokenFromURL(); // Handles token extraction from URL
    useTemplatesInit(); // Fetch templates
    useUserInit(); // Fetch user
    useHubSpot();
    useSentryUser()
    useHandleVisibility()

    const handleLogout = async () => {
        try {
            await auth.signOut();
            clearAuth();
            console.info("User logged out successfully.");
        } catch (error) {
            console.error("Logout failed:", error);
            showMessage("Er is een fout opgetreden bij het uitloggen.", "error");
        }
    };

    const handleMenuSelect = (menuItem: MenuItem) => {
        setSelectedMenu(menuItem);
        setTimestampKey(Date.now());  // to force re-render menu item on each click
    };

    const handleSidebarToggle = () => {
        setSidebarOpen(!sidebarOpen);
    };

    if (!idToken) return <Login/>;

    if (userModel && !userModel?.accepted_agreement) return <UserAgreement/>;
    if (userModel && !userModel?.accepted_privacy_policy) return <PrivacyPolicy/>;
    if (userModel && !userModel?.accepted_usage_instructions) return <UsageInstructions/>;

    // Calculate current sidebar width based on state
    const currentDrawerWidth = sidebarOpen ? drawerWidth : 0;

    return (
        <Box sx={{display: 'flex'}}>
            <CssBaseline/>
            <GlobalSnackbar/>
            <Sidebar
                drawerWidth={drawerWidth}
                onMenuSelect={handleMenuSelect}
                handleLogout={handleLogout}
                open={sidebarOpen}
                onToggle={handleSidebarToggle}
            />
            <AppBar
                position="fixed"
                sx={{
                    height: 64,
                    width: {
                        xs: '100%',
                        md: `calc(100% - ${currentDrawerWidth}px)`
                    },
                    ml: {
                        xs: 0,
                        md: currentDrawerWidth
                    },
                    bgcolor: "background.default",
                    color: "text.primary",
                    boxShadow: "none",
                    borderBottom: "1px solid",
                    borderColor: "divider",
                    borderRadius: 0,
                    padding: 0,
                    transition: theme.transitions.create(['margin', 'width'], {
                        easing: theme.transitions.easing.sharp,
                        duration: theme.transitions.duration.leavingScreen,
                    }),
                }}
            >
                <Toolbar
                    sx={{
                        display: 'flex',
                        justifyContent: {
                            xs: 'center',
                            md: 'flex-start'
                        }
                    }}
                >

                    {isIntegration ?
                        <img src={medendoLogo} alt="Logo" width={180} style={{marginLeft: '20px'}}/>
                        :
                        <Typography variant="h6" noWrap>
                            {selectedMenuItem}
                        </Typography>}
                </Toolbar>
            </AppBar>
            <Box
                component="main"
                maxWidth="xl"
                sx={{
                    flexGrow: 1,
                    p: 2,
                    width: {
                        xs: '100%',
                        md: `calc(100% - ${currentDrawerWidth}px)`
                    },
                    mt: 10,
                    transition: theme.transitions.create(['width'], {
                        easing: theme.transitions.easing.sharp,
                        duration: theme.transitions.duration.leavingScreen,
                    }),
                }}
            >
                {selectedMenuItem === MenuItem.NewEncounter &&
                    <Encounter key={timestampKey} id={null} sidebarOpen={sidebarOpen}/>}
                {selectedMenuItem === MenuItem.History && (
                    <EncountersList key={timestampKey} sidebarOpen={sidebarOpen}/>)}
                {selectedMenuItem === MenuItem.Templates && (<TemplatesList key={timestampKey}/>)}
            </Box>
        </Box>
    );
};

export default App;
